function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

// 新加的
class ShareUtil {
  static generatePrefix () {
    // select the checked input of the model 
    const modelNode = document.querySelector('.variant-option-model input:checked');
    const modelInputs = document.querySelectorAll('.variant-option-model input');
  
    let colorSelectPrefix = '';
    let modelIndex  = 0;
    if (modelNode && modelInputs.length > 0) {
      modelIndex = modelNode.getAttribute('data-index');
      if (modelInputs.length > 2) {
        if (modelIndex === '1' || modelIndex === 1) {
          colorSelectPrefix = '1_';
        } else if (modelIndex === '2' || modelIndex === 2) {
          colorSelectPrefix = '2_';
        }
      } else {
        if (modelIndex === '1' || modelIndex === 1 )colorSelectPrefix = '1_';
      }
    }
    return colorSelectPrefix;
  }
  
  static isMobile() {
    var userAgentInfo = navigator.userAgent;
    var mobileAgents = [ "Android", "iPhone", "SymbianOS", "Windows Phone", "iPad","iPod"];
    var mobile_flag = false;
    for (var v = 0; v < mobileAgents.length; v++) {
        if (userAgentInfo.indexOf(mobileAgents[v]) > 0) {
            mobile_flag = true;
            break;
        }
    }
    var screen_width = window.innerWidth;
    var screen_height = window.screen.height;
    if(screen_width < 990){
        mobile_flag = true;
    }
    return mobile_flag;
  }
}

// 新加的
class CartService {
  constructor () {
    this.customAddCartItem = [];
    this.defaultItems = [];
    this.extraAccToCartFnCreator();
  }
  
  execute (executor) {
    executor(this.customAddCartItem, this);
  }
  
  getCustomAddCartItem () {
    return this.customAddCartItem;
  }
  
  getDefaultItems () {
    return this.defaultItems;
  }
  
  extraAccToCartFnCreator () {
    window.addExtraAccToCartFn = window.addExtraAccToCartFn || function addExtraAccToCartFn(cart) {
      const customAddCartItem = window.Shopify.cartService.getCustomAddCartItem();
      const defaultItems = window.Shopify.cartService.getDefaultItems();
      const accItems = customAddCartItem.concat(defaultItems);
      
      console.log(accItems)
      if (accItems.length) {
        let param = {
          sections: cart.getSectionsToRender().map((section) => section.id),
          items: accItems,
        };
  
        return fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(param),
        });
      }
      return null;
    };
  }
  
  addItemToCart (items) {
    if (typeof items === 'undefined') return;
    if (!Array.isArray(items)) {
      items = [items]
    }
    
    this.customAddCartItem = this.customAddCartItem.concat(
      items.map(function (id) {
        return {
          id,
          quantity: 1,
        };
      })
    );
  }
  
  removeItemFromCart (items) {
    if (typeof items === 'undefined') return;
    if (!Array.isArray(items)) {
      items = [items]
    }
    
    for (let i = 0; i < this.customAddCartItem.length; i++) {
      for (let k = 0; k < items.length && i >= 0; k++) {
        if (this.customAddCartItem[i]["id"] === items[k]) {
          this.customAddCartItem.splice(i, 1);
          i--;
        }
      }
    }
  }
}

const cartServiceCreaterFn = function cartServiceCreater () {
  if (!window.Shopify.cartService) {
    window.Shopify.cartService = new CartService();;
  }
}
cartServiceCreaterFn();

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);


// 新加的
class ModalDialog1 extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target.nodeName === "MODAL-DIALOG1") this.hide();
      });
    }
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog1", ModalDialog1);


class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

// 新版改造后
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector("ul");
    this.sliderItems = this.querySelectorAll("li");
    this.pageCount = this.querySelector(".slider-counter--current");
    this.pageTotal = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.productSlider = this.querySelector(".product-slider-box");
    
    this.defaultVariantPrefix = this.getAttribute("data-default-variant-prefix");
    
    this.initThumbnail(this.defaultVariantPrefix);

    if (!this.slider || !this.nextButton) return;

    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    const sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    this.sliderLastItem = sliderItemsToShow[sliderItemsToShow.length - 1];
    if (sliderItemsToShow.length === 0) return;
    this.slidesPerPage = Math.floor(
      this.slider.clientWidth / sliderItemsToShow[0].clientWidth
    );
    if (this.productSlider) {
      this.totalPages = sliderItemsToShow.length - this.slidesPerPage + 4;
    } else {
      this.totalPages = sliderItemsToShow.length - this.slidesPerPage + 1;
    }
    this.update();
  }

  update() {
    if (!this.pageCount || !this.pageTotal) return;
    if (this.productSlider) {
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderLastItem.clientWidth) + 4;
    } else {
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderLastItem.clientWidth) + 1;
    }

    if (this.currentPage === 1) {
      this.prevButton.setAttribute("disabled", true);
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (this.currentPage === this.totalPages) {
      this.nextButton.setAttribute("disabled", true);
    } else {
      this.nextButton.removeAttribute("disabled");
    }

    this.pageCount.textContent = this.currentPage;
    this.pageTotal.textContent = this.totalPages;
  }

  onButtonClick(event) {
    event.preventDefault();
    const slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + this.sliderLastItem.clientWidth
        : this.slider.scrollLeft - this.sliderLastItem.clientWidth;
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
  
  initThumbnail (defaultVariantPrefix) {
    $('[thumbnail-color]').hide();
    const colorSelectPrefix = defaultVariantPrefix || ShareUtil.generatePrefix();
    let Upcolor = `${colorSelectPrefix}${document.getElementById('color_label').innerHTML.toLowerCase().replace(/(^\s*)|(\s*$)/g, "" ).replace(" ","_")}`;
    const U_color = document.getElementById('color_label');

    
    window.globalData = {
      currentProductThumbsIndex: 0
    }
    
    $("#color_fieldset input").each(function(){
      $(this).click(function(e){
        var name = $(this).attr('value');
        U_color.innerHTML = name;
        window.globalData['currentProductThumbsIndex'] = 0;
      });
    });
    
    $('slider-component').on('click', 'li.slide-image-handler', (event) => {
      const target = event.currentTarget;
      this.onThumbnailClick(target);
    });
    
    const color_attribute = '[thumbnail-color="' + Upcolor + '"]';
    const FirstsThumbnail = $(color_attribute).first();
    $(color_attribute).show();
    
    FirstsThumbnail.click();
  }
  
  onThumbnailClick (x) {
    let clickedImageMediaId = x.dataset.mediaId;
    const newImage = document.querySelector(`.product__media-item-handler[data-media-id="${clickedImageMediaId}"]`);
    const parentData = newImage.parentElement;

    parentData.prepend(newImage);
    newImage.style.display = "block";

    document.querySelectorAll('.slide-image-handler').forEach(thumb => thumb.classList.remove('active-thumb'));
    document.querySelector(`.slide-image-handler[data-media-id="${clickedImageMediaId}"]`).classList.add('active-thumb');
    updateIndex(clickedImageMediaId);
  }
}

customElements.define("slider-component", SliderComponent);

// 新加的配件轮播图组件
class SliderComponentAccessory extends SliderComponent {
  
  constructor() {
    super();
  }
  
  initThumbnail () {
    document.querySelector("li:nth-child(1) > img").classList.add('active-thumb');
    
    $('slider-component-accessory').on('click', 'li.slider__slide', (event) => {
      const target = event.currentTarget;
      this.onThumbnailClick(target);
    });
  }
  
  onThumbnailClick (x) {
    let clickedImage = x.dataset.mediaId;
    const newImage = document.querySelector(`[data-media-id="${clickedImage}"]`);
    const parentData = newImage.parentElement;

    console.log(clickedImage);

    parentData.prepend(newImage);
    newImage.style.display = "block";
  
    document.querySelectorAll('.slide-image').forEach(thumb => thumb.classList.remove('active-thumb'));
    document.querySelector(`[data-media-id="${clickedImage}"] > img`).classList.add('active-thumb');
  };
}

customElements.define("slider-component-accessory", SliderComponentAccessory);


class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

// 新加改版的
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    // the variant data init
    this.getVariantData();
    // bind the change event of variant input
    this.addEventListener("change", this.onVariantChange);
  }

  before() {}

  after() {}

  onVariantChange(e) {
    this.before(e);
    this.updateOptions(e);
    this.renderVariantOptions();
    this.updateMasterId();
    this.toggleAddButton(true, "", false);
    this.updatePickupAvailability();
    this.removeErrorMessage();

    if (!this.currentVariant) {
      this.toggleAddButton(true, "", true);
      this.setUnavailable();
    } else {
      this.updateMedia();
      this.filterMedia();
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
    }

    this.after();
  }

  updateOptions() {
    this.options = Array.from(
      this.querySelectorAll("select"),
      (select) => select.value
    );
  }

  updateMasterId() {
    this.currentVariant = this.variantData.find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateMedia() {
    if (!this.currentVariant) return;
    if (!this.currentVariant.featured_media) return;
    const newMedia = document.querySelector(
      `[data-media-id="${this.dataset.section}-${this.currentVariant.featured_media.id}"]`
    );

    if (!newMedia) return;
    const modalContent = document.querySelector(
      `#ProductModal-${this.dataset.section} .product-media-modal__content`
    );
    const newMediaModal = modalContent.querySelector(
      `[data-media-id="${this.currentVariant.featured_media.id}"]`
    );
    const parent = newMedia.parentElement;
    if (parent.firstChild == newMedia) return;
    modalContent.prepend(newMediaModal);
    parent.prepend(newMedia);
    this.stickyHeader =
      this.stickyHeader || document.querySelector("sticky-header");
    if (this.stickyHeader) {
      this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
    }
    // window.setTimeout(() => { parent.querySelector('li.product__media-item').scrollIntoView({behavior: "smooth"}); });
  }

  filterMedia() {
    $("[thumbnail-color]").hide();

    var selected_variant = this.currentVariant.featured_media.alt;

    var selected_attribute = '[thumbnail-color="' + selected_variant + '"]';

    var FirstThumbnail = $(selected_attribute).first();

    if (selected_variant == selected_variant) {
      FirstThumbnail.click();
      $(selected_attribute).show();
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === "false") return;
    window.history.replaceState(
      {},
      "",
      `${this.dataset.url}?variant=${this.currentVariant.id}`
    );
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event("change", { bubbles: true }));
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector("pickup-availability");
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute("available");
      pickUpAvailability.innerHTML = "";
    }
  }

  removeErrorMessage() {
    const section = this.closest("section");
    if (!section) return;

    const productForm = section.querySelector("product-form");
    if (productForm) productForm.handleErrorMessage();
  }

  renderPrice(html) {
    const id = `price-${this.dataset.section}`;
    const destination = document.querySelectorAll(".product-price-handler");
    const source = html.getElementById(id);

    if (source && destination.length > 0) {
      for (let i = 0; i < destination.length; i++) {
        destination[i].innerHTML = source.innerHTML;
      }
    }
  }

  renderProductInfo() {
    fetch(
      `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`
    )
      .then((response) => response.text())
      .then((responseText) => {
        const id = `price-${this.dataset.section}`;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        const destination = document.getElementById(id);
        const source = html.getElementById(id);

        if (source && destination) destination.innerHTML = source.innerHTML;

        this.renderPrice(html);

        const price = document.getElementById(`price-${this.dataset.section}`);
        const metafields = document.getElementById(
          `metafields-${this.dataset.section}`
        );
        const delivery = document.getElementById(
          `delivery-${this.dataset.section}`
        );
        const coupons = document.getElementById(
          `coupons-${this.dataset.section}`
        );
        const coupons_eu = document.getElementById(
          `coupons_eu-${this.dataset.section}`
        );

        if (price) price.classList.remove("visibility-hidden");
        if (metafields)
          metafields.classList.remove("visibility-hidden"),
            this.updateMetafields(html);
        if (delivery)
          delivery.classList.remove("visibility-hidden"),
            this.updateDelivery(html);
        if (coupons)
          coupons.classList.remove("visibility-hidden"),
            this.updateCoupons(html);
        if (coupons_eu)
          coupons_eu.classList.remove("visibility-hidden"),
            this.updateCouponsEu(html);
        this.toggleAddButton(
          !this.currentVariant.available,
          window.variantStrings.soldOut
        );

        this.renderSaleItem(html);
      });
  }

  renderVariantOptions() {}

  renderSaleItem(html) {
    const productSaleTabsContainer = document.querySelector(
      ".extra-acc-container"
    );
    const sourceTabs = html.querySelectorAll("product-sale-tab");

    const saleTabList =
      productSaleTabsContainer.querySelectorAll("product-sale-tab");
    saleTabList.forEach((child) => {
      child.remove();
      child.removeHandle();
    });

    for (let i = 0; i < sourceTabs.length; i++) {
      productSaleTabsContainer.appendChild(sourceTabs[i]);
    }
  }

  updateMetafields(html) {
    const id = `metafields-${this.dataset.section}`;
    const destination = document.getElementById(id);
    const source = html.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
  }

  updateDelivery(html) {
    const id = `delivery-${this.dataset.section}`;
    const destination = document.getElementById(id);
    const source = html.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
  }

  updateCoupons(html) {
    const id = `coupons-${this.dataset.section}`;
    const destination = document.getElementById(id);
    const source = html.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
  }

  updateCouponsEu(html) {
    const id = `coupons_eu-${this.dataset.section}`;
    const destination = document.getElementById(id);
    const source = html.getElementById(id);

    if (source && destination) destination.innerHTML = source.innerHTML;
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(
      `product-form-${this.dataset.section}`
    );
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');

    if (!addButton) return;

    if (disable) {
      addButton.setAttribute("disabled", true);
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute("disabled");
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(
      `product-form-${this.dataset.section}`
    );
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const metafields = document.getElementById(
      `metafields-${this.dataset.section}`
    );
    const delivery = document.getElementById(
      `delivery-${this.dataset.section}`
    );
    const coupons = document.getElementById(`coupons-${this.dataset.section}`);
    const coupons_eu = document.getElementById(
      `coupons_eu-${this.dataset.section}`
    );
    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add("visibility-hidden");
    if (metafields) metafields.classList.add("visibility-hidden");
    if (delivery) delivery.classList.add("visibility-hidden");
    if (coupons) coupons.classList.add("visibility-hidden");
    if (coupons_eu) coupons_eu.classList.add("visibility-hidden");
  }

  getVariantData() {
    this.variantData =
      this.variantData ||
      JSON.parse(this.querySelector(".product-variants-list").textContent);
    return this.variantData;
  }
}

customElements.define("variant-selects", VariantSelects);

// 新加的
class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  updateOptions() {
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));
    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll("input")).find(
        (radio) => radio.checked
      ).value;
    });
  }
}

customElements.define("variant-radios", VariantRadios);

// 新加的
class VariantRadiosEnhance extends VariantSelects {
  constructor() {
    super();
    this.initOptionListObject();
    this.updateOptions();
    this.renderVariantOptions();
    this.validateExistVariantOption();
  }

  initOptionListObject() {
    const variantData = JSON.parse(
      this.querySelector("#product-variants-info").textContent
    );


    const optionsListObject = {};
    const optionList = [];

    for (let i = 0; i < variantData.length; i++) {
      const { options, available, inventory_quantity } = variantData[i];
      optionsListObject[options.join("/").trim()] = {
        available,
        inventory_quantity: inventory_quantity,
        len: options.length,
      };
      optionList.push(options);
    }

    this.optionsListObject = optionsListObject;
    this.optionList = optionList;
  }

  // Needing to verify that the options selected are present.
  updateOptions(e) {
    const fieldsets = Array.from(this.querySelectorAll("fieldset"));

    this.options = fieldsets.map((fieldset) => {
      return Array.from(fieldset.querySelectorAll("input")).find(
        (radio) => radio.checked
      ).value;
    });

    if (e) {
      const optionsKey = this.options.join("/").trim();
      // if the selected option was not present, you needed to update the option
      if (!this.optionsListObject[optionsKey]) {
        // Record the option you selected at this moment.
        const target = e.target;
        const val = target.value;
        const option = this.findFirstExsitVariant(val);
        if (option) {
          this.options = option.split("/");
        }
      }
    }
  }

  // Base the current selected option, find the frist variant at the option list
  findFirstExsitVariant(val) {
    const ret = null;

    const keys = Object.keys(this.optionsListObject);

    for (let i = 0; i < keys.length; i++) {
      const option = keys[i];
      if (option.includes(val)) {
        return option;
      }
    }

    return ret;
  }

  // Based on the selected option, update the list of variant options.
  renderVariantOptions() {
    this.renderVariantOptionList();
    this.renderSelectOption();
  }

  // When the user has selected an option, and this causes another previously selected option at a diffrence level to be hidden,
  // we should set the first option that was not hidden to the selected state.
  renderSelectOption() {
    // need to select option by postion
    const radiosWrappers = document.querySelectorAll(
      "variant-radios-enhance .variant-option-wrapper"
    );
    radiosWrappers.forEach((wrapper) => {
      const radiosItems = wrapper.querySelectorAll(
        '.variant-option-item:not(.unavailable) input[type="radio"]'
      );
      const options = this.options;

      for (let i = 0; i < radiosItems.length; i++) {
        if (options.includes(radiosItems[i].value)) {
          radiosItems[i].checked = true;
          $(radiosItems[i]).trigger("change");
        }
      }
    });

    this.updateOptions();
    this.updateUpColor();
  }

  updateUpColor() {
    var U_color = document.getElementById("color_label");
    const modelNode = document.querySelector("#color_fieldset input:checked");
    U_color.innerHTML = $(modelNode).attr("value");
  }

  /**
   * Remove options that don't have any variants.
   */
  validateExistVariantOption() {
    const optionList = this.optionList;
    const radiosTitemList = document.querySelectorAll(
      `variant-radios-enhance .variant-option-wrapper:not([data-lock]) .variant-option-item`
    );
    radiosTitemList.forEach((element) => {
      const value = element.getAttribute("data-value");
      const pos = element.getAttribute("data-pos");
      let flag = false;
      for (let i = 0; i < optionList.length; i++) {
        if (optionList[i][+pos - 1].includes(value)) {
          flag = true;
        }
      }

      if (!flag) {
        element.classList.add("hidden");
      } else {
        element.classList.remove("hidden");
      }
    });
  }

  renderVariantOptionList() {
    const options = this.options;
    const optionList = this.optionList;

    // core logic:
    // Since only one option can be selected at a time, the options that can be selected at each level are controlled by other additional attributes.
    // Then you only need to remove the currently selected options on each layer, and then use other options to get the options that can be selected on the current layer.
    // Then cycle through each layer.
    if (this.options) {
      let optionFilterMap = {};
      for (let i = 0; i < options.length; i++) {
        let condition = [];
        for (let t = 0; t < options.length; t++) {
          if (t !== i) {
            condition.push(options[t]);
          }
        }

        let tempRes = [];
        for (let k = 0; k < optionList.length; k++) {
          let elements = optionList.filter((item) =>
            condition.every((element) => item.includes(element))
          );
          for (let j = 0; j < elements.length; j++) {
            if (!tempRes.includes(elements[j][i])) {
              tempRes.push(elements[j][i]);
            }
          }
        }
        optionFilterMap[i] = tempRes;
      }

      Object.keys(optionFilterMap).forEach((key) => {
        const radiosWrapper = document.querySelector(
          `variant-radios-enhance .variant-option-wrapper[data-pos="${
            +key + 1
          }"]`
        );


        const existVariants = optionFilterMap[key];
        const radios = radiosWrapper.querySelectorAll('input[type="radio"]');

        for (let i = 0; i < radios.length; i++) {
          const radio = radios[i];
          if (existVariants.includes(radio.value)) {
            radio.parentNode.classList.remove("unavailable");
          } else {
            radio.parentNode.classList.add("unavailable");
          }
        }
      });
    }
  }

  filterAndRemoveOptions(variants, options) {
    return variants
      .filter((variant) => {
        return options.every((option) => variant.includes(option));
      })
      .map((variant) => {
        return variant.filter((option) => !options.includes(option));
      })
      .flat();
  }

  removeKeyFromArray(arr, key) {
    return arr.filter((item) => item !== key);
  }

  getCheckedOptionValue(pos) {
    const radiosWrapper = document.querySelectorAll(
      `variant-radios-enhance .variant-option-wrapper:not([data-lock]) .variant-option-item:not([data-pos="${pos}"])`
    );
    const values = [];
    radiosWrapper.forEach((radioWrapper) => {
      const radio = radioWrapper.querySelector('input[type="radio"]');
      if (radio.checked) {
        values.push(radio.value);
      }
    });

    return values;
  }
}

customElements.define("variant-radios-enhance", VariantRadiosEnhance);

// 新加的
function updateIndex(targetId) {
  const colorSelectPrefix = ShareUtil.generatePrefix();
  let Upcolor = `${colorSelectPrefix}${document
    .getElementById("color_label")
    .innerHTML.toLowerCase()
    .replace(/(^\s*)|(\s*$)/g, "")
    .replace(" ", "_")}`;

  const color_attribute = '[thumbnail-color="' + Upcolor + '"]';
  const thumbNodeList = $(color_attribute);

  for (let index = 0; index < thumbNodeList.length; index++) {
    if (thumbNodeList[index].dataset.mediaId === targetId) {
      window.globalData["currentProductThumbsIndex"] = index;
    }
  }
}

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}



// 新加的
class ProductSale extends HTMLElement {
  constructor() {
    super();
    this.hideAssociateVariantsList = ($(this).data("hide-associate-vaiants") || "") + "";
    // The variant id of the associated Ebikie product
    this.variantId = $(this).data("variant-id") + "";
  }
}

// 新加的
// class ProductSaleTab extends ProductSale {
//   constructor() {
//     super();
//     this.init();
//     this.event();
//   }

//   addAccByDefault() {
//     this.items = $(this).data("variant-list") + "";
//     if (typeof this.items === "undefined") {
//       return;
//     }
//     this.default_selected = $(this).data("deafult-selected");
//     this.itemsArr = this.items.split(",");

//     // selected by default
//     if (this.default_selected) {
//       console.log(this.itemsArr)
//       window.Shopify.cartService.addItemToCart(this.itemsArr);
//     }
//   }

//   init() {
//     let associateColor = $(this).data("associate-color");
//     // if the associateColor is exist
//     if (associateColor) {
//       const possibleNames = [ "color", "kleur", "colour", "couleur", "colore", "farbe", "farve" ];
//       const checkedNode = Array.from(
//         document.querySelectorAll('input[type="radio"]')
//       ).find((input) =>
//         possibleNames.includes(input.name.toLowerCase()) && input.checked
//       );
      
//       let checkedColor = "";
//       if (checkedNode) {
//         checkedColor = checkedNode.value.toLowerCase();
//       }
//       if (checkedColor === associateColor.toLowerCase()) {
//         this.addAccByDefault();
//       } else {
//         this.querySelector(".pannel-content").classList.remove("selected");
//       }
//     } else {
//       this.addAccByDefault();
//     }

//     if (
//       !this.hideAssociateVariantsList ||
//       this.hideAssociateVariantsList.trim() === ""
//     ) {
//       return;
//     }

//     if (
//       this.hideAssociateVariantsList &&
//       this.hideAssociateVariantsList !== "" &&
//       this.hideAssociateVariantsList.split(",").includes(this.variantId)
//     ) {
//       if ($(this).data("hide-type") === 0) {
//         this.remove();
//         this.removeHandle();
//       } else {
//         this.querySelector(".pannel-content-operate").remove();
//         this.querySelector(".pannel-content").classList.remove("user-select");
//       }
//     }
//   }

//   event() {
//     this.addEventListener("click", () => {
//       if (!$(this).data("readonly") && !$(this).attr("disabled")) {
//         if (
//           this.hideAssociateVariantsList &&
//           this.hideAssociateVariantsList !== "" &&
//           this.hideAssociateVariantsList.split(",").includes(this.variantId)
//         ) {
//           return;
//         }
//         const container = this.querySelector(".pannel-content-sale-item");
//         if (container.classList.contains("selected")) {
//           this.querySelector(".pannel-content-sale-item").classList.remove(
//             "selected"
//           );
//           window.Shopify.cartService.removeItemFromCart(this.itemsArr);
//         } else {
//           this.querySelector(".pannel-content-sale-item").classList.add(
//             "selected"
//           );
//           window.Shopify.cartService.addItemToCart(this.itemsArr);
//         }
//         console.log(this.itemsArr);
//       }
//     });
//   }

//   removeHandle() {
//     window.Shopify.cartService.removeItemFromCart(this.itemsArr);
//   }
// }
// customElements.define("product-sale-tab", ProductSaleTab);

class ProductSaleTab extends ProductSale {
  constructor() {
    super();

    this.items = $(this).data("variant-list") + "";
    this.itemsArr = this.items ? this.items.split(",") : [];
    this.default_selected = $(this).data("deafult-selected");
    this.isAvailable =
      $(this).data("available") === true ||
      $(this).data("available") === "true";

    this.init();
    this.event();
  }

  addAccByDefault() {
    if (!this.default_selected) return;
    if (!this.isAvailable) return;

    this.querySelector(".pannel-content-sale-item")?.classList.add("selected");
    window.Shopify.cartService.addItemToCart(this.itemsArr);
  }

  init() {
    // 🚫 没库存：直接禁用，不参与任何逻辑
    if (!this.isAvailable) {
      this.classList.add("is-sold-out");
      this.setAttribute("disabled", "disabled");
      this.querySelector(".pannel-content")?.classList.remove("selected");
      return;
    }

    let associateColor = $(this).data("associate-color");

    if (associateColor) {
      const possibleNames = [
        "color",
        "kleur",
        "colour",
        "couleur",
        "colore",
        "farbe",
        "farve",
      ];

      const checkedNode = Array.from(
        document.querySelectorAll('input[type="radio"]')
      ).find(
        (input) =>
          possibleNames.includes(input.name.toLowerCase()) && input.checked
      );

      let checkedColor = checkedNode
        ? checkedNode.value.toLowerCase()
        : "";

      if (checkedColor === associateColor.toLowerCase()) {
        this.addAccByDefault();
      } else {
        this.querySelector(".pannel-content")?.classList.remove("selected");
      }
    } else {
      this.addAccByDefault();
    }

    // ===== 你原有的 hide 逻辑（保持不动）=====
    if (
      this.hideAssociateVariantsList &&
      this.hideAssociateVariantsList !== "" &&
      this.hideAssociateVariantsList.split(",").includes(this.variantId)
    ) {
      if ($(this).data("hide-type") === 0) {
        this.remove();
        this.removeHandle();
      } else {
        this.querySelector(".pannel-content-operate")?.remove();
        this.querySelector(".pannel-content")?.classList.remove("user-select");
      }
    }
  }

  event() {
    this.addEventListener("click", () => {
      if (
        $(this).data("readonly") ||
        $(this).attr("disabled") ||
        !this.isAvailable
      ) {
        return;
      }

      const container = this.querySelector(".pannel-content-sale-item");

      if (container.classList.contains("selected")) {
        container.classList.remove("selected");
        window.Shopify.cartService.removeItemFromCart(this.itemsArr);
      } else {
        container.classList.add("selected");
        window.Shopify.cartService.addItemToCart(this.itemsArr);
      }
    });
  }

  removeHandle() {
    window.Shopify.cartService.removeItemFromCart(this.itemsArr);
  }
}

customElements.define("product-sale-tab", ProductSaleTab);


